import React, { useEffect, useRef, useState } from 'react';
import { ToastContainer } from '@lwt-helix/toast';
import Loader from '@lwt-helix/loader';
import { setStateData } from '../../common/helpers/state';
import { getConfig, getCoverageConfig } from '../helpers/configGenerator';
import { getEffectiveListingType, getRowCount, getShowHideKey } from '../helpers/helpers';
import { listingTypes, marketShareTerms, showHideTypes } from '../constants';
import { modules, searchTerms, toastMessages } from '../../constants';
import TitleRow from './common/TitleRow';
import { ResultsChart } from './ResultsChart';
import MarketShareTable from './ResultsTable';
import { getExportButton } from '../../common/helpers/export';
import { exportTypes } from '../helpers/export';
import { getBottomRowSettings } from '../helpers/agGrid';
import { getListingStatus } from '../helpers/listingStatus';
import { useLocation } from 'react-router-dom';
import { getPinnedRowRouteSettings } from '../../common/routes/routes';
import { useCommonStore, useSearchStore } from '../../store/store';
import { NoResultsMessage } from '../../common/components/search/NoResultsMessage';
import { useComparisonSetsStore } from '../../store/comparisonSets/store';
import { getAllOtherOfficesIds } from '../helpers/officesBreakdown';
import { exportButtonTypes } from '../../common/constants/export';
import { DownloadResultsModal } from '../../common/components/DownloadResultsModal';
import { usePDFStore } from '../../store/pdf/store';
import { showToast, toastTypes } from '../../common/helpers/toast';

const MarketShare = ({ module, agentData }) => {
    const {
        areaTypeTitle,
        loadedSearchCriteria,
        isLoadingSearchData,
        selectedComparisonSetSearch,
        isSelectedCompSetAllOtherGroupClickable,
        listingType,
        currentListingViewId,
        showHideOptions,
        selectedColumnId,
        setMSselectedColumnId,
        setMSshowHideOptions,
        shouldShowChart,
    } = useSearchStore(state => ({
        areaTypeTitle: state[module]?.areaTypeTitle,
        loadedSearchCriteria: state[module]?.loadedSearchCriteria,
        selectedMls: state[module]?.selectedMls,
        isLoadingSearchData: state.isLoadingSearchData,
        selectedComparisonSetSearch: state[module]?.selectedComparisonSetSearch,
        isSelectedCompSetAllOtherGroupClickable: state[module]?.isSelectedCompSetAllOtherGroupClickable,
        listingType: state[module]?.listingType,
        currentListingViewId: state[module]?.listingViewId,
        showHideOptions: state[module]?.showHideOptions,
        selectedColumnId: state[module]?.selectedColumnId,
        setMSselectedColumnId: state.setMSselectedColumnId,
        setMSshowHideOptions: state.setMSshowHideOptions,
        shouldShowChart: state[module]?.shouldShowChart,
    }));

    const {
        setListingViewTitle,
        setResults,
        setColumnDefs,
        isPDFLoading,
    } = usePDFStore(state => ({
        setListingViewTitle: state.setListingViewTitle,
        setResults: state.setResults,
        setColumnDefs: state.setColumnDefs,
        isPDFLoading: state.downloadModalStatus.isPDFLoading,
    }));

    const {
        isComparisonSetView,
    } = useComparisonSetsStore(state => ({
        isComparisonSetView: state.isComparisonSetView
    }));

    const [state, setState] = useState({
        offices: [],
        brokers: [],
        comparisonSets: [],
        columnDefs: [],
        titleContent: null,
        isShowExportBtn: false,
        selectedRowsIds: [],
        toast: {},
        tostProps: null,
        coverageSearchData: [],
        selectedAreaType: null,
    });

    const listingViewId = currentListingViewId || state.titleContent?.listingViews?.find(lv => lv.isDefault)?.id;

    const comparisonSetsData = state.comparisonSets?.results;

    const [rowData, setRowData] = useState();
    const [coverageRowData, setCoverageRowData] = useState();

    const listingStatus = getListingStatus(module, loadedSearchCriteria);

    const isCoverageSearch = module === modules.marketShare.coverage;

    const effectiveListingType = getEffectiveListingType(isCoverageSearch, isComparisonSetView, listingType);
    const currentShowHideOptions = showHideOptions?.[getShowHideKey(effectiveListingType, listingViewId)] || {options: [], columns: []};


    const gridRef = useRef();
    const location = useLocation();

    const hasData = !!(state.offices || state.brokers || comparisonSetsData);

    useEffect(() => {
        if (location.state?.fromValidation) {
            showToast(toastTypes.success, toastMessages.success.agentValidated, setState);
        }
    }, []);

    useEffect(() => {
        setStateData(
            'selectedAreaType',
            areaTypeTitle?.mainTitle.slice(searchTerms.selectedAreaTypeTitle.length),
            setState
        );
    }, [areaTypeTitle]);

    useEffect(() => {
        if (!loadedSearchCriteria) return
        setStateData('searchCriteria', loadedSearchCriteria, setState);
    }, [loadedSearchCriteria]);

    useEffect(() => {
        setState(state => ({
            ...state,
            offices: agentData?.officeSearch,
            brokers: agentData?.brokerSearch,
            coverageSearchData: agentData?.coverageSearch,
            comparisonSets: agentData?.comparisonSetSearch,
        }));
    }, [agentData]);

    useEffect(() => {
        if (isCoverageSearch) {
            if (!listingStatus) return;
        } else {
            if (!listingStatus ||
                !hasData)
                return;
        }
        let titleContent, coverageColumnDefs;

        if (isCoverageSearch) {

            titleContent = getCoverageConfig({
                listingStatus: listingStatus,
                listingViewId: listingViewId,
                data: state.coverageSearchData?.results,
                module
            });
        } else {
            titleContent = getConfig({
                listingStatus,
                listingTypeId: isComparisonSetView ? listingTypes.comparisonSet.id : listingType,
                listingViewId: listingViewId,
                data: {
                    offices: state.offices?.results,
                    brokers: state.brokers?.results,
                    comparisonSetData: comparisonSetsData,
                },
                module,
                isComparisonSetView
            });
        }

        if (isCoverageSearch && titleContent?.tableSettings) {
            let areaColumn;
            let updatedAreaColumn;
            areaColumn = titleContent.tableSettings?.columns.find(column => column.field === 'area');
            updatedAreaColumn = { ...areaColumn, headerName: state.selectedAreaType };
            coverageColumnDefs = titleContent.tableSettings?.columns.map(column => {
                if (column.field === 'area') {
                    return { ...column, ...updatedAreaColumn };
                }

                return column;
            });
        }

        const hasDefaultShowHideOptions = currentShowHideOptions?.options?.length
            && currentShowHideOptions?.columns?.length;

        if (!hasDefaultShowHideOptions) {
            const options = titleContent.showHideOptions.options;
            const columns = titleContent.showHideOptions.columns;

            setMSshowHideOptions(effectiveListingType, listingViewId, { options: options, columns: columns });
        }
        let columnDefs = isCoverageSearch
            ? coverageColumnDefs
            : titleContent?.tableSettings.columns;
        if (currentShowHideOptions?.columns?.length && columnDefs?.length) {
            columnDefs = columnDefs.map(col => {
                const matchingOption = currentShowHideOptions.columns.find(option => option.id === col.field
                    || (col.groupId && option.groupId === col.groupId));
                if (matchingOption !== undefined) {
                    return { ...col, hide: matchingOption.hide };
                }
                return col;
            });
        }

        setColumnDefs(columnDefs);
        setListingViewTitle(titleContent.rank);
        setState(prevState => ({
            ...prevState,
            titleContent,
            columnDefs,
        }));
    }, [state.offices, state.brokers, listingType, listingViewId,
    state.coverageSearchData, listingStatus, isComparisonSetView, comparisonSetsData, module]);

    useEffect(() => {
        if(!selectedColumnId) return;
        const clickedColumn = currentShowHideOptions?.columns?.find(col => col.id === selectedColumnId);
        const columnDefs = state.columnDefs?.map(col => {
            if (col.field === clickedColumn?.id) {
                return { ...col, hide: !!clickedColumn?.hide };
            }
            if (col.groupId && col.groupId === clickedColumn?.groupId) {
                return { ...col, hide: !!clickedColumn?.hide };
            }
            return col;
        });
        if (!columnDefs?.length > 0) return;
        setMSselectedColumnId(null);
        setState(prevState => ({
            ...prevState,
            columnDefs
        }));
        setColumnDefs(columnDefs);
    }, [currentShowHideOptions?.columns]);

    const setSelectedRowsIds = data => {
        setStateData('selectedRowsIds', data, setState);
        useCommonStore.setState({ selectedRowsIds: data });
    };

    useEffect(() => {
        if (isCoverageSearch ||
            !state?.titleContent?.dataTransforms ||
            !hasData ) return;

        const listingTypeId = listingType || listingTypes.byOffice.id;
        const supplementalData = {
            listingViewId: state?.titleContent?.listingViews?.find(lv => lv.id === listingViewId)?.keyField ||
                state?.titleContent?.listingViews?.find(lv => lv.isDefault).keyField,
            listingTypeId: isComparisonSetView ? listingTypes.byBrokerage.id : listingTypeId,
            sumOfColumn: state?.titleContent?.sumOfColumns?.[isComparisonSetView ? listingTypes.comparisonSet.id : listingTypeId],
            rowCount: getRowCount(state[listingTypes[listingTypeId].collectionKey])
        };

        let loopTransformedData = {
            offices: state.offices?.results.map(office => ({ ...office, ...supplementalData })) ?? [],
            brokers: state.brokers?.results.map(broker => ({ ...broker, ...supplementalData })) ?? [],
            comparisonSetData: comparisonSetsData?.map((group, i) => ({ ...group, supplementalData, groupId: i })) ?? [],
        };

        state.titleContent?.dataTransforms?.forEach(transform => {
            loopTransformedData = transform(loopTransformedData);
        });

        setState(prevState => ({
            ...prevState,
            selectedRowsIds: loopTransformedData?.filter(row => row.isSelected === true).map(row => row.officeId || row.brokerId || row.groupId),
            isShowExportBtn: !!loopTransformedData?.length
        }));

        setRowData(loopTransformedData?.length ? loopTransformedData : null);
        setResults(loopTransformedData?.length ? loopTransformedData : null);
        setTimeout(() => {
            useCommonStore.setState({ selectedRowsIds: loopTransformedData?.length
                ? loopTransformedData?.filter(row => row.isSelected === true).map(row => row.officeId || row.brokerId || row.groupId)
                : null
            });
        }, 500);
    }, [state.titleContent, state.offices, state.brokers, listingType,
        listingViewId, isCoverageSearch, comparisonSetsData]);

    useEffect(() => {
        if (!isCoverageSearch ||
            !state?.titleContent?.dataTransforms ||
            !state.coverageSearchData) return;

        const supplementalData = {
            listingViewId: state?.titleContent?.listingViews?.find(lv => lv.id === listingViewId)?.keyField ||
                state?.titleContent?.listingViews?.find(lv => lv.isDefault).keyField,
            sumOfColumn: state?.titleContent?.sumOfColumn,
            rowCount: state.titleContent?.rowCount
        };
        let transformedData = {
            data: (state.coverageSearchData?.results ?? []).map(entry => ({ ...entry, ...supplementalData }))
        };
        state.titleContent?.dataTransforms?.forEach(transform => {
            transformedData = transform(transformedData);
        });

        setSelectedRowsIds(transformedData?.filter(row => row.isSelected === true).map(row => row.areaId));
        setCoverageRowData(transformedData?.length && transformedData[0]?.marketSharePct
            ? transformedData
            : null);
        setResults(transformedData?.length && transformedData[0]?.marketSharePct
            ? transformedData
            : null);    
    }, [state.titleContent, state.coverageSearchData, isCoverageSearch, listingViewId]);

    useEffect(() => {
        if (!isLoadingSearchData) return;
        
        const fetchOfficesIds = async () => {
            const officesIds = await getAllOtherOfficesIds(selectedComparisonSetSearch);
            useSearchStore.setState(prevState => ({
                ...prevState,
                [module]: {
                    ...prevState[module],
                    isSelectedCompSetAllOtherGroupClickable: officesIds?.length > 0
                }
            }));
        };
    
        fetchOfficesIds();
    }, [isLoadingSearchData]);

    const makeRowLinkClickable = (row, rowId) => ({
        ...row,
        isSelected: state.selectedRowsIds?.includes(rowId),
        isClickable: row.groupName === marketShareTerms.allOther
            ? isSelectedCompSetAllOtherGroupClickable
            : true
    });

    const updatedRowData = rowData?.map(row => {
        const rowId = row.officeId || row.brokerId || row.groupId;
        return makeRowLinkClickable(row, rowId);
    });

    const updatedCoverageSearchData = coverageRowData?.map(row => {
        const rowId = row.areaId;
        return { ...row, isSelected: state.selectedRowsIds?.includes(rowId) };
    });
    const isShowSelectedRows = currentShowHideOptions?.options &&
        !currentShowHideOptions?.options.find(option => option.id === showHideTypes.onlyShowSelectedOffices.id
            || option.id === showHideTypes.onlyShowSelectedBrokerages.id
            || option.id === showHideTypes.onlyShowSelectedAreas.id
            || option.id === showHideTypes.onlyShowSelectedGroups.id)?.hide;

    const showToastDeprecated = toastType => {
        let toastProperties;
        if (toastType === marketShareTerms.MAX_ROWS_ARE_SELECTED) {
            toastProperties = {
                id: 1,
                message: marketShareTerms.maxReached,
                color: 'danger',
                icon: 'error_outline_icon',
                border: 'left',
                dismissTime: 3000,
            };
        }
        // The second setState prevents the toast from reappearing on a subsequent search.
        // The toast object gets reset, but this does not remove an already visible toast
        setStateData('toast', toastProperties, setState);
        setTimeout(() => setStateData('toast', {}, setState), 500);
    };

    const getStatusMessage = ({ hasInitialSearchRun, messageContent }) => {
        return <>
            <TitleRow
                titleContent={state.titleContent}
                module={module}
                hasInitialSearchRun={hasInitialSearchRun}
            />
            <div className='text-center'>
                {messageContent}
            </div>
            <ToastContainer
            dataLwtId='marketShare-toast'
            toastProps={state.toastProps}
            position={'toast-bottom-right'}
        />
        </>;
    };

    const pinnedRowDrilldownSettingsType = getPinnedRowRouteSettings(location.pathname);
    let updatedData, searchNotRunMessage, chartData, bottomRowParams, resultsTableBottomRowData,
        bottomRowType;
    if (isCoverageSearch) {
        bottomRowType = 'coverageSearch';
        chartData = updatedCoverageSearchData?.filter(row => row.isSelected);
        updatedData = updatedCoverageSearchData;
        searchNotRunMessage = marketShareTerms.searchFilterAdjustCoverage;
        resultsTableBottomRowData = state.coverageSearchData?.totals;
        bottomRowParams = {
            bottomRowType,
            pinnedRowDrilldownSettingsType,
            gridRef,
            rowData: coverageRowData,
            pinnedRowData: resultsTableBottomRowData
        };
    } else {
        bottomRowType = 'totalsSearch';
        chartData = updatedRowData?.filter(row => row.isSelected);
        updatedData = updatedRowData;
        searchNotRunMessage = marketShareTerms.searchFilterAdjust;
        const nonComparisonSetsTotals = state[listingTypes[listingType]?.collectionKey ||
            listingTypes.byOffice?.collectionKey]?.totals;
        resultsTableBottomRowData = isComparisonSetView ? state.comparisonSets?.totals : nonComparisonSetsTotals;
        bottomRowParams = {
            bottomRowType,
            pinnedRowDrilldownSettingsType,
            gridRef,
            rowData,
            pinnedRowData: resultsTableBottomRowData
        };
    }
    const initialSearchNotRun = getStatusMessage({
        hasInitialSearchRun: false,
        messageContent: <p className='initial-search'> {searchNotRunMessage}</p>
    });

    const noResultsFound = getStatusMessage({
        hasInitialSearchRun: true,
        messageContent: <>
            <NoResultsMessage />
        </>
    });

    const chart = <ResultsChart
        data={chartData}
        config={state.titleContent}
        hasSelectedRows={state.selectedRowsIds?.length}
    />;

    const pdfChart = <ResultsChart
        data={chartData}
        config={state.titleContent}
        hasSelectedRows={state.selectedRowsIds?.length}
        isRenderingToFile={isPDFLoading}
    />;

    const hasRowData = rowData || coverageRowData;
    let exportButton;
    if (hasRowData) {
        const exportDetails = {
            exportType: exportTypes.msSearchResults,
            listingStatus: state.titleContent?.rank,
            searchType: state.titleContent?.searchType,
            listingType: state.titleContent?.listingTypes?.find(listing => listing.isDefault)?.label
        };
        exportButton = getExportButton({
            exportDetails,
            gridRef,
            type: exportButtonTypes.secondary
        });
    }

    const resultsFound = <>
        <TitleRow
            titleContent={state.titleContent}
            module={module}
            exportButton={exportButton}
            hasInitialSearchRun={true}
            isComparisonSetView={isComparisonSetView}
            isCoverageSearch={isCoverageSearch}
        />
        <DownloadResultsModal />
        {listingStatus && hasRowData && updatedData &&
        <>
            {shouldShowChart && chart}
            {shouldShowChart && isPDFLoading && <div className='pdf-chart chart-capture-area'>
                {pdfChart}
            </div>
            }
            <MarketShareTable
                gridRef={gridRef}
                rowData={updatedData}
                columnDefs={state.columnDefs}
                setSelectedRowsIds={setSelectedRowsIds}
                showToast={showToastDeprecated}
                isShowSelectedRows={isShowSelectedRows}
                listingStatus={listingStatus}
                rank={state.titleContent?.rank}
                hasHeaderTooltips={true}
                selectedRowsIds={state.selectedRowsIds}
                getBottomRowSettings={() => bottomRowParams && getBottomRowSettings(bottomRowParams)}
            />
        </>
        }
        <ToastContainer
            dataLwtId='marketShare-toast'
            toastProps={state.toast}
            position={'toast-bottom-right'}
        />
    </>;

    const loader = <div className='py-3'><Loader /></div>;

    const isInitialSearchNotRun = loadedSearchCriteria === undefined;

    const hasResults = (isCoverageSearch && listingStatus && !!state.coverageSearchData?.results?.length)
        || (listingStatus && (!!state.brokers?.results?.length || !!state.offices?.results?.length))
        || comparisonSetsData?.length;

    const searchWithNoResults = (isCoverageSearch && state.coverageSearchData?.results?.length === 0)
        || (state.offices?.results?.length === 0 || state.brokers?.results?.length === 0)
        || comparisonSetsData?.length === 0;

    if (isLoadingSearchData) {
        return loader;
    }

    if (isInitialSearchNotRun) {
        return initialSearchNotRun;
    }
    if (hasResults) {
        return resultsFound;
    }
    if (searchWithNoResults) {
        return noResultsFound;
    }

    return loader;
};

export default MarketShare;
